import { ArrowUpRight } from "lucide-react"

import { cn } from "~/lib/utils"
import { ForestBackdrop } from "./ForestBackdrops"

// "Pine59 banner" (Figma "Landing Page — Internal Assets", 291:1738). A forest
// band directly below the nav: the topo-grid data texture under a forest wash,
// a product screenshot bleeding up from the bottom edge on the left, and a
// pitch + link on the right. Scrolls with the page.
//
// Copy defaults to the homepage's Console & API announcement; /get-started
// reuses it as its closing band with a softer "still deciding?" line, and a
// post can swap in a different `image`/`href` entirely (e.g. Insights)
// while keeping the same shell.
type BannerImage = {
  src: string
  srcSet?: string
  sizes?: string
  alt: string
  width: number
  height: number
}

const DOCS_IMAGE: BannerImage = {
  src: "/product/docs-guides.webp",
  srcSet: "/product/docs-guides-700.webp 700w, /product/docs-guides.webp 1400w",
  sizes: "(min-width: 1024px) 50vw, 100vw",
  alt: "The Pine59 documentation site: the Guides & examples page, with how-to guides like Brand Comparison, Store Profiling and Area Identification",
  width: 1400,
  height: 489,
}

export function DocsBanner({
  image = DOCS_IMAGE,
  href = "https://console.pine59.com/docs",
  body = "Introducing Pine59 Console & API, with open documentation. Explore practical guides, real-world examples, datasets, concepts, and APIs.",
  ctaLabel = "Start building",
  onCtaClick,
  imageRing = "ring-white/10",
  imageFade = true,
}: {
  image?: BannerImage
  href?: string
  body?: string
  /** Omit to drop the CTA link entirely — just the pitch copy. */
  ctaLabel?: string | null
  onCtaClick?: () => void
  /** Frame accent around the screenshot — the subtle default, or e.g.
      "ring-lime/60" for a product (like Insights) that carries its own
      lime accent elsewhere. Pass "" when `image` already bakes its own
      border in (skips the ring entirely). */
  imageRing?: string
  /** Set false when `image` already fades to forest at its own bottom
      edge, so this doesn't layer a second gradient on top. */
  imageFade?: boolean
} = {}) {
  const external = /^https?:\/\//.test(href)
  return (
    <section className="relative overflow-hidden border-t border-white/5 bg-forest text-cream">
      <ForestBackdrop />

      <div className="relative u-container grid gap-8 pt-10 lg:grid-cols-2 lg:items-center lg:gap-14 lg:pt-12">
        {/* product shot — rounded top, bleeds to the band's bottom edge and
            fades out into the forest */}
        <div className="relative order-2 self-end lg:order-1">
          {/* Frame ratio matches docs-guides.webp's own dimensions, so that
              image renders unconstrained while a taller shot (e.g. Insights)
              gets object-cover'd down to the same compact strip — only its
              top half shows, same as the Console screenshot. */}
          <div
            className={cn(
              "aspect-[1400/489] overflow-hidden rounded-t-[16px]",
              imageRing && cn("ring-1", imageRing),
            )}
          >
            {/* Sits in the first phone viewport, so loading="lazy" here was
                showing a blank flash before it painted in. Load eagerly but
                at low fetch priority instead — starts fetching immediately
                (no flash) without competing with the two real LCP
                candidates above for bandwidth. */}
            <img
              src={image.src}
              srcSet={image.srcSet}
              sizes={image.sizes}
              alt={image.alt}
              width={image.width}
              height={image.height}
              loading="eager"
              fetchPriority="low"
              decoding="async"
              className="block h-full w-full object-cover object-top"
            />
          </div>
          {imageFade ? (
            <div
              className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-b from-transparent to-forest"
              aria-hidden
            />
          ) : null}
        </div>

        {/* pitch + CTA */}
        <div className="order-1 pb-10 lg:order-2 lg:pb-12">
          <p className="max-w-xl font-sans text-[18px] font-normal leading-tight tracking-[-0.01em] text-cream sm:text-[20px]">
            {body}
          </p>
          {ctaLabel ? (
            <a
              href={href}
              {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
              onClick={onCtaClick}
              className="mt-6 inline-flex items-center gap-1 text-[14px] font-medium text-cream/90 transition-colors hover:text-chartreuse"
            >
              {ctaLabel}
              <ArrowUpRight className="h-4 w-4 shrink-0" strokeWidth={1.75} />
            </a>
          ) : null}
        </div>
      </div>
    </section>
  )
}
